import React, {useContext, useState, useEffect, useMemo, useCallback, useRef} from 'react';
import {Context} from '../contexts/WordList';
import {useLoadWords} from '../effects/useLoadWords';
import {fetchStudyProgress, saveStudyProgress, fetchDueWords} from '../helpers/localApi';

const SWIPE_THRESHOLD = 80;

const MASTERY_MASTERED = 4;

// Graded recall: 0=Again, 1=Hard, 2=Good, 3=Easy.
const GRADES = [
  {id: 0, label: 'Again', className: 'study-btn-again'},
  {id: 1, label: 'Hard', className: 'study-btn-hard'},
  {id: 2, label: 'Good', className: 'study-btn-good'},
  {id: 3, label: 'Easy', className: 'study-btn-easy'},
];

export const StudyMode = () => {
  const [state, dispatch] = useContext(Context);
  const {items, language} = state;

  useEffect(() => {
    dispatch({type: 'HAS_MORE_ITEMS', payload: true});
  }, [dispatch]);

  useLoadWords();

  // Progress: map of wordId -> {masteryLevel, correctCount, incorrectCount}
  const [progressMap, setProgressMap] = useState({});
  const [progressLoaded, setProgressLoaded] = useState(false);

  useEffect(() => {
    if (!language) return;
    fetchStudyProgress({language})
      .then((data) => {
        const map = {};
        data.forEach((p) => {
          map[p.wordId] = p;
        });
        setProgressMap(map);
        setProgressLoaded(true);
      })
      .catch(() => setProgressLoaded(true));
  }, [language]);

  // Filter state
  const [activeFilter, setActiveFilter] = useState('all');
  const [sessionProgressMap, setSessionProgressMap] = useState({});
  // Word IDs currently due for review (loaded when the Due filter is used).
  const [dueIds, setDueIds] = useState(null);

  useEffect(() => {
    setDueIds(null);
  }, [language]);

  useEffect(() => {
    if (activeFilter !== 'due' || dueIds || !language) return;
    let cancelled = false;
    fetchDueWords({language})
      .then((rows) => {
        if (!cancelled) {
          const ids = {};
          (rows || []).forEach((row) => {
            ids[row.wordId] = true;
          });
          setDueIds(ids);
        }
      })
      .catch(() => {
        if (!cancelled) setDueIds({});
      });
    return () => {
      cancelled = true;
    };
  }, [activeFilter, dueIds, language]);

  const progressMapRef = useRef(progressMap);
  useEffect(() => {
    progressMapRef.current = progressMap;
  }, [progressMap]);

  useEffect(() => {
    if (!progressLoaded) return;
    // Freeze progress for this study session so cards do not reorder mid-run.
    setSessionProgressMap(progressMapRef.current);
  }, [progressLoaded, activeFilter, items, language]);

  // Available tags from loaded items
  const availableTags = useMemo(() => {
    const tags = new Set();
    items.forEach((item) => (item.tags || []).forEach((t) => tags.add(t)));
    return Array.from(tags).sort();
  }, [items]);

  // KPI counts
  const kpi = useMemo(() => {
    let mastered = 0;
    let learning = 0;
    let newWords = 0;
    items.forEach((item) => {
      const p = progressMap[item.key];
      if (!p || p.masteryLevel === 0) newWords++;
      else if (p.masteryLevel >= MASTERY_MASTERED) mastered++;
      else learning++;
    });
    return {mastered, learning, new: newWords};
  }, [items, progressMap]);

  // Filtered deck
  const deck = useMemo(() => {
    let filtered = items.slice();
    if (activeFilter === 'due') {
      // Due queue: scheduled words + words never studied (new).
      filtered = filtered.filter((item) => {
        const p = sessionProgressMap[item.key];
        if (!p || !p.masteryLevel) return true;
        return Boolean(dueIds && dueIds[item.key]);
      });
    } else if (activeFilter === 'new') {
      filtered = filtered.filter((item) => {
        const p = sessionProgressMap[item.key];
        return !p || p.masteryLevel === 0;
      });
    } else if (activeFilter === 'unmastered') {
      filtered = filtered.filter((item) => {
        const p = sessionProgressMap[item.key];
        return !p || p.masteryLevel < MASTERY_MASTERED;
      });
    } else if (activeFilter !== 'all') {
      // tag filter
      filtered = filtered.filter((item) =>
        (item.tags || []).includes(activeFilter),
      );
    }
    // Shuffle
    for (let i = filtered.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const tmp = filtered[i];
      filtered[i] = filtered[j];
      filtered[j] = tmp;
    }
    return filtered;
  }, [items, sessionProgressMap, activeFilter, dueIds]);

  // Words scheduled for review (or never studied) — powers the Due filter chip.
  const dueCount = useMemo(() => {
    const nowIso = new Date().toISOString();
    return items.filter((item) => {
      const p = progressMap[item.key];
      if (!p || !p.masteryLevel) return true;
      return Boolean(p.dueAt && p.dueAt <= nowIso);
    }).length;
  }, [items, progressMap]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [known, setKnown] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  const touchStartRef = useRef(null);
  const wasDraggingRef = useRef(false);
  const [dragX, setDragX] = useState(0);

  useEffect(() => {
    setCurrentIndex(0);
    setFlipped(false);
    setKnown(0);
    setIsComplete(false);
  }, [deck]);

  const safeIndex = deck.length > 0 ? Math.min(currentIndex, deck.length - 1) : 0;
  const currentCard = deck[safeIndex];

  // Response timing: measures from card reveal to answer (personalised SRS input).
  const cardShownAtRef = useRef(null);
  useEffect(() => {
    cardShownAtRef.current = Date.now();
  }, [safeIndex, flipped]);

  const advance = useCallback((grade) => {
    if (currentCard) {
      const responseMs = cardShownAtRef.current
        ? Date.now() - cardShownAtRef.current
        : null;
      saveStudyProgress({
        wordId: currentCard.key,
        language,
        grade,
        responseMs,
      }).then((updated) => {
        if (updated) {
          setProgressMap((prev) => ({...prev, [updated.wordId]: updated}));
        }
      }).catch(() => {});
    }

    if (grade >= 2) setKnown((prev) => prev + 1);
    setFlipped(false);
    setDragX(0);

    if (currentIndex + 1 >= deck.length) {
      setIsComplete(true);
    } else {
      setCurrentIndex((prev) => prev + 1);
    }
  }, [currentIndex, deck.length, currentCard, language]);

  const handleRestart = () => {
    setCurrentIndex(0);
    setFlipped(false);
    setKnown(0);
    setIsComplete(false);
  };

  const handleCardTouchStart = (e) => {
    if (e.touches.length !== 1) return;
    touchStartRef.current = {x: e.touches[0].clientX, y: e.touches[0].clientY};
    wasDraggingRef.current = false;
  };

  const handleCardTouchMove = (e) => {
    if (!touchStartRef.current || e.touches.length !== 1) return;
    const dx = e.touches[0].clientX - touchStartRef.current.x;
    const dy = e.touches[0].clientY - touchStartRef.current.y;
    if (Math.abs(dx) > Math.abs(dy) * 1.3) {
      const clamped = Math.sign(dx) * Math.min(Math.abs(dx), 150);
      setDragX(clamped);
      if (Math.abs(dx) > 8) wasDraggingRef.current = true;
    }
  };

  const handleCardTouchEnd = () => {
    const savedDragX = dragX;
    const wasDrag = wasDraggingRef.current;
    touchStartRef.current = null;
    wasDraggingRef.current = false;
    setDragX(0);
    if (wasDrag && Math.abs(savedDragX) >= SWIPE_THRESHOLD) {
      // Right swipe = Good, left swipe = Again.
      advance(savedDragX > 0 ? 2 : 0);
    }
  };

  const handleCardClick = () => {
    if (wasDraggingRef.current) {
      wasDraggingRef.current = false;
      return;
    }
    setFlipped((prev) => !prev);
  };

  if (!progressLoaded) {
    return (
      <div className="study-container">
        <div className="study-empty">
          <p>Loading progress…</p>
        </div>
      </div>
    );
  }

  if (deck.length === 0 && items.length === 0) {
    return (
      <div className="study-container">
        <div className="study-empty">
          <div style={{fontSize: 52, marginBottom: 12}}><span role="img" aria-label="cards">🎴</span></div>
          <h2>No words to study</h2>
          <p>Add some vocabulary words first, then come back to study them.</p>
        </div>
      </div>
    );
  }

  if (isComplete) {
    const pct = deck.length > 0 ? Math.round((known / deck.length) * 100) : 0;
    const notKnown = deck.length - known;
    return (
      <div className="study-container">
        <div className="study-kpi-bar">
          <div className="study-kpi-item study-kpi-mastered">
            <span className="study-kpi-value">{kpi.mastered}</span>
            <span className="study-kpi-label">Mastered</span>
          </div>
          <div className="study-kpi-item study-kpi-learning">
            <span className="study-kpi-value">{kpi.learning}</span>
            <span className="study-kpi-label">Learning</span>
          </div>
          <div className="study-kpi-item study-kpi-new">
            <span className="study-kpi-value">{kpi.new}</span>
            <span className="study-kpi-label">New</span>
          </div>
        </div>
        <div className="study-complete">
          <div style={{fontSize: 52}}><span role="img" aria-label="result">{pct >= 80 ? '🎉' : pct >= 50 ? '👍' : '💪'}</span></div>
          <div className="study-score">{pct}%</div>
          <h2>Session Complete!</h2>
          <p>You knew {known} out of {deck.length} words.</p>
          <div className="study-complete-stats">
            <div className="study-stat">
              <span className="study-stat-value" style={{color: 'var(--green)'}}>{known}</span>
              <span className="study-stat-label">Known</span>
            </div>
            <div className="study-stat">
              <span className="study-stat-value" style={{color: 'var(--red)'}}>{notKnown}</span>
              <span className="study-stat-label">Review</span>
            </div>
            <div className="study-stat">
              <span className="study-stat-value">{deck.length}</span>
              <span className="study-stat-label">Total</span>
            </div>
          </div>
          <button type="button" className="btn btn-primary" style={{width: '100%', justifyContent: 'center'}} onClick={handleRestart}>
            Study Again
          </button>
        </div>
      </div>
    );
  }

  const progress = deck.length > 0 ? (safeIndex / deck.length) * 100 : 0;
  const cardStyle = dragX !== 0 ? {
    transform: `rotate(${dragX * 0.04}deg) translateX(${dragX * 0.25}px)`,
    transition: 'none',
  } : {};
  const showKnow = dragX > 30;
  const showSkip = dragX < -30;

  const currentMastery = currentCard ? (progressMap[currentCard.key]?.masteryLevel ?? 0) : 0;

  return (
    <div className="study-container">
      {/* KPI bar */}
      <div className="study-kpi-bar">
        <div className="study-kpi-item study-kpi-mastered">
          <span className="study-kpi-value">{kpi.mastered}</span>
          <span className="study-kpi-label">Mastered</span>
        </div>
        <div className="study-kpi-item study-kpi-learning">
          <span className="study-kpi-value">{kpi.learning}</span>
          <span className="study-kpi-label">Learning</span>
        </div>
        <div className="study-kpi-item study-kpi-new">
          <span className="study-kpi-value">{kpi.new}</span>
          <span className="study-kpi-label">New</span>
        </div>
      </div>

      {/* Filter bar */}
      <div className="study-filter-bar" role="group" aria-label="Study filter">
        {[
          {id: 'all', label: `All (${items.length})`},
          {id: 'due', label: `Due (${dueCount})`},
          {id: 'new', label: `New (${kpi.new})`},
          {id: 'unmastered', label: `Unmastered (${kpi.learning + kpi.new})`},
          ...availableTags.map((t) => ({id: t, label: t})),
        ].map(({id, label}) => (
          <button
            key={id}
            type="button"
            className={`study-filter-chip${activeFilter === id ? ' active' : ''}`}
            onClick={() => setActiveFilter(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {deck.length === 0 ? (
        <div className="study-empty">
          <div style={{fontSize: 52, marginBottom: 12}}><span role="img" aria-label="celebration">🎉</span></div>
          <h2>{activeFilter === 'due' ? 'Nothing due!' : 'All done!'}</h2>
          <p>{activeFilter === 'due'
            ? 'No words are scheduled for review right now. Check back later or study all words.'
            : 'No words match the current filter.'}</p>
          <button type="button" className="btn btn-primary" style={{marginTop: 12}} onClick={() => setActiveFilter('all')}>
            Study All Words
          </button>
        </div>
      ) : (
        <>
          <div className="study-progress">
            <div className="study-progress-bar">
              <div className="study-progress-fill" style={{width: `${progress}%`}} />
            </div>
            <span className="study-progress-text">{safeIndex + 1} / {deck.length}</span>
          </div>

          {flipped && (
            <div className="study-swipe-hint">
              <span className="study-swipe-hint-know">← Good</span>
              <span className="study-swipe-hint-skip">Again →</span>
            </div>
          )}

          <div
            className="flashcard-shell"
            onTouchStart={handleCardTouchStart}
            onTouchMove={handleCardTouchMove}
            onTouchEnd={handleCardTouchEnd}
            onTouchCancel={handleCardTouchEnd}
          >
            <div className={`flashcard-swipe-indicator flashcard-swipe-know${showKnow ? ' visible' : ''}`} aria-hidden="true">✓ Good</div>
            <div className={`flashcard-swipe-indicator flashcard-swipe-skip${showSkip ? ' visible' : ''}`} aria-hidden="true">✗ Again</div>

            <div
              className={`flashcard${flipped ? ' flipped' : ''}`}
              style={cardStyle}
              onClick={handleCardClick}
              role="button"
              tabIndex={0}
              aria-label={flipped ? 'Card is flipped. Swipe or use buttons below.' : 'Tap to reveal translation'}
              onKeyDown={(e) => {
                if (e.key === ' ' || e.key === 'Enter') setFlipped((prev) => !prev);
              }}
            >
              <div className="flashcard-inner">
                <div className="flashcard-front">
                  <div className="flashcard-label">{state.orderBy === 'wordEn' ? 'English' : state.language}</div>
                  <div className={`flashcard-word${state.orderBy !== 'wordEn' ? ` ${state.language === 'farsi' ? 'font-farsi' : 'font-hebrew'}` : ''}`}>
                    {state.orderBy === 'wordEn' ? currentCard.wordEn : currentCard.wordFa}
                  </div>
                  <div className="flashcard-hint">Tap to reveal ✦</div>
                </div>
                <div className="flashcard-back">
                  <div className="flashcard-label">{state.orderBy === 'wordEn' ? state.language : 'English'}</div>
                  <div className={`flashcard-word${state.orderBy === 'wordEn' ? ` ${state.language === 'farsi' ? 'font-farsi' : 'font-hebrew'}` : ''}`}>
                    {state.orderBy === 'wordEn' ? currentCard.wordFa : currentCard.wordEn}
                  </div>
                  {currentCard.transliteration && (
                    <div className="flashcard-transliteration">{currentCard.transliteration}</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Mastery indicator */}
          <div className="study-mastery-row" aria-label={`Mastery level ${currentMastery} of 5`}>
            {[1, 2, 3, 4, 5].map((level) => (
              <span
                key={level}
                className={`study-mastery-dot${level <= currentMastery ? ' filled' : ''}`}
              />
            ))}
          </div>

          <div className="study-actions">
            {!flipped ? (
              <>
                <button type="button" className="study-btn study-btn-unsure" onClick={() => advance(0)}>
                  ✗ Don&apos;t Know
                </button>
                <button type="button" className="study-btn study-btn-know" onClick={() => advance(2)}>
                  ✓ Know It
                </button>
              </>
            ) : (
              GRADES.map(({id, label, className}) => (
                <button
                  key={id}
                  type="button"
                  className={`study-btn study-btn-graded ${className}`}
                  onClick={() => advance(id)}
                >
                  {label}
                </button>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};
